/**
 * Setup screen — voice-guided medication setup wizard (6 steps).
 * Step 1: Say medication name -> fuzzy search
 * Step 2: Confirm drug match -> show pill photo
 * Step 3: Frequency selection (big buttons)
 * Step 4: Time picker with presets
 * Step 5: Special instructions (voice or skip)
 * Step 6: Confirm and save
 */

import type { Drug, MedFrequency, PatientMedication } from '../../types/index';
import { searchDrugsByName } from '../../core/drug-store';
import { addPatientMedication } from '../../core/db';
import { speak } from '../../voice/tts';
import { createVoiceButton } from '../components/voice-button';
import { showNotification } from '../components/notification-card';
import { t } from '../../i18n';

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function esc(v: string): string { return v.replace(/'/g, "''"); }

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------

interface SetupState {
  step: number;
  searchQuery: string;
  selectedDrug: Drug | null;
  frequency: MedFrequency;
  times: string[];
  instructions: string;
}

const TOTAL_STEPS = 6;

function defaultState(): SetupState {
  return {
    step: 1,
    searchQuery: '',
    selectedDrug: null,
    frequency: 'once_daily',
    times: ['08:00'],
    instructions: '',
  };
}

// -----------------------------------------------------------------------
// Progress indicator
// -----------------------------------------------------------------------

function renderProgress(step: number): HTMLElement {
  const row = document.createElement('div');
  row.className = 'progress-steps';
  row.setAttribute('role', 'progressbar');
  row.setAttribute('aria-valuenow', String(step));
  row.setAttribute('aria-valuemin', '1');
  row.setAttribute('aria-valuemax', String(TOTAL_STEPS));
  row.setAttribute('aria-label', t('setup.progress', { n: step, total: TOTAL_STEPS }));

  for (let i = 1; i <= TOTAL_STEPS; i++) {
    const dot = document.createElement('div');
    dot.className = 'progress-dot';
    if (i === step) dot.classList.add('active');
    if (i < step) dot.classList.add('complete');
    row.appendChild(dot);
  }

  const label = document.createElement('span');
  label.className = 'text-muted';
  label.style.cssText = 'margin-left: var(--space-md); font-size: var(--font-size-small);';
  label.textContent = t('setup.progress', { n: step, total: TOTAL_STEPS });
  row.appendChild(label);

  return row;
}

// -----------------------------------------------------------------------
// Step renderers
// -----------------------------------------------------------------------

function renderStep1(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const heading = document.createElement('h2');
  heading.className = 'text-center mb-md';
  heading.textContent = t('setup.step1.title');
  wrapper.appendChild(heading);

  const hint = document.createElement('p');
  hint.className = 'text-muted text-center mb-lg';
  hint.textContent = t('setup.step1.hint');
  wrapper.appendChild(hint);

  // Text input
  const inputGroup = document.createElement('div');
  inputGroup.className = 'flex-col gap-md';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = t('setup.step1.placeholder');
  input.setAttribute('aria-label', t('setup.step6.medication'));
  input.value = state.searchQuery;
  input.style.cssText = `
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    font-size: var(--font-size-body);
    font-family: var(--font-family);
    border: 2px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-elevated);
    color: var(--color-text);
    outline: none;
  `;

  input.addEventListener('focus', () => {
    input.style.borderColor = 'var(--color-accent)';
  });
  input.addEventListener('blur', () => {
    input.style.borderColor = 'var(--color-border)';
  });

  // Search results container
  const resultsDiv = document.createElement('div');
  resultsDiv.className = 'flex-col gap-sm';
  resultsDiv.setAttribute('role', 'listbox');
  resultsDiv.setAttribute('aria-label', t('setup.searchResults'));

  let searchTimeout: ReturnType<typeof setTimeout> | null = null;

  async function performSearch(query: string): Promise<void> {
    state.searchQuery = query;
    resultsDiv.textContent = '';

    if (query.length < 2) return;

    let results: Drug[] = [];
    try {
      results = await searchDrugsByName(query);
    } catch {
      return;
    }

    if (results.length === 0) {
      const noResult = document.createElement('p');
      noResult.className = 'text-dim text-center';
      noResult.textContent = t('setup.noResults');
      resultsDiv.appendChild(noResult);
      return;
    }

    for (const drug of results.slice(0, 8)) {
      const option = document.createElement('div');
      option.className = 'card card-interactive';
      option.setAttribute('role', 'option');
      option.setAttribute('tabindex', '0');
      option.setAttribute('aria-label', drug.generic_name + (drug.brand_names.length > 0 ? ', also known as ' + drug.brand_names[0] : ''));
      option.style.cssText = 'padding: var(--space-md); cursor: pointer;';

      const nameEl = document.createElement('div');
      nameEl.style.cssText = 'font-size: var(--font-size-large); font-weight: 700; color: var(--color-text);';
      nameEl.textContent = drug.generic_name;
      option.appendChild(nameEl);

      if (drug.brand_names.length > 0) {
        const brandEl = document.createElement('div');
        brandEl.className = 'text-muted';
        brandEl.textContent = drug.brand_names.join(', ');
        option.appendChild(brandEl);
      }

      const selectDrug = (): void => {
        state.selectedDrug = drug;
        state.step = 2;
        rerender();
      };

      option.addEventListener('click', selectDrug);
      option.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectDrug();
        }
      });

      resultsDiv.appendChild(option);
    }
  }

  input.addEventListener('input', () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      performSearch(input.value.trim());
    }, 300);
  });

  inputGroup.appendChild(input);
  inputGroup.appendChild(resultsDiv);
  wrapper.appendChild(inputGroup);

  // Voice button for spoken input
  const voiceSection = document.createElement('div');
  voiceSection.className = 'text-center mt-lg';

  const voiceButton = createVoiceButton(
    () => {
      voiceButton.setState('listening');
      document.dispatchEvent(new CustomEvent('aftercare:voice-start'));
    },
    () => {
      voiceButton.setState('processing');
      document.dispatchEvent(new CustomEvent('aftercare:voice-stop'));
    },
  );
  voiceSection.appendChild(voiceButton.root);
  wrapper.appendChild(voiceSection);

  // Listen for transcription results
  const onTranscript = (e: Event): void => {
    const text = (e as CustomEvent).detail?.text ?? '';
    if (text) {
      input.value = text;
      voiceButton.setState('idle');
      performSearch(text);
    }
  };
  document.addEventListener('aftercare:transcript', onTranscript);

  // Announce for screen readers
  speak(t('setup.step1.speak'));

  // If there was a previous search, re-run it
  if (state.searchQuery) {
    performSearch(state.searchQuery);
  }
}

function renderStep2(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const drug = state.selectedDrug!;

  const heading = document.createElement('h2');
  heading.className = 'text-center mb-md';
  heading.textContent = t('setup.step2.title');
  wrapper.appendChild(heading);

  // Pill card
  const card = document.createElement('div');
  card.className = 'card text-center';
  card.style.padding = 'var(--space-lg)';

  // Pill image
  const imgWrapper = document.createElement('div');
  imgWrapper.className = 'mb-md';
  const img = document.createElement('img');
  img.src = drug.pill_image_path || '/pill-images/' + drug.id + '.webp';
  img.alt = drug.generic_name + ' pill';
  img.style.cssText = 'width: 120px; height: 120px; object-fit: contain; border-radius: var(--radius-md);';
  img.onerror = () => {
    img.onerror = null;
    img.src = '/pill-images/default-pill.svg';
  };
  imgWrapper.appendChild(img);
  card.appendChild(imgWrapper);

  const nameEl = document.createElement('h3');
  nameEl.textContent = drug.generic_name;
  card.appendChild(nameEl);

  if (drug.brand_names.length > 0) {
    const brandEl = document.createElement('p');
    brandEl.className = 'text-muted';
    brandEl.textContent = drug.brand_names.join(', ');
    card.appendChild(brandEl);
  }

  if (drug.pill_color || drug.pill_shape) {
    const detailEl = document.createElement('p');
    detailEl.className = 'text-dim';
    const parts = [drug.pill_color, drug.pill_shape].filter(Boolean);
    detailEl.textContent = parts.join(', ');
    card.appendChild(detailEl);
  }

  wrapper.appendChild(card);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.className = 'flex-col gap-md mt-lg';

  const yesBtn = document.createElement('button');
  yesBtn.className = 'btn btn-success btn-lg btn-block';
  yesBtn.textContent = t('setup.step2.yes');
  yesBtn.setAttribute('aria-label', t('setup.step2.yes'));
  yesBtn.addEventListener('click', () => {
    state.step = 3;
    rerender();
  });
  btnRow.appendChild(yesBtn);

  const noBtn = document.createElement('button');
  noBtn.className = 'btn btn-outline btn-lg btn-block';
  noBtn.textContent = t('setup.step2.no');
  noBtn.setAttribute('aria-label', t('setup.step2.no'));
  noBtn.addEventListener('click', () => {
    state.selectedDrug = null;
    state.step = 1;
    rerender();
  });
  btnRow.appendChild(noBtn);

  wrapper.appendChild(btnRow);

  speak(t('setup.step2.speak', { drug: drug.generic_name }));
}

function renderStep3(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const heading = document.createElement('h2');
  heading.className = 'text-center mb-md';
  heading.textContent = t('setup.step3.title');
  wrapper.appendChild(heading);

  const options: [MedFrequency, string][] = [
    ['once_daily', t('setup.step3.once')],
    ['twice_daily', t('setup.step3.twice')],
    ['three_daily', t('setup.step3.thrice')],
    ['four_daily', t('setup.step3.four')],
    ['every_other_day', t('setup.step3.everyOther')],
    ['weekly', t('setup.step3.weekly')],
    ['as_needed', t('setup.step3.asNeeded')],
  ];

  const grid = document.createElement('div');
  grid.className = 'flex-col gap-sm';

  for (const [value, label] of options) {
    const btn = document.createElement('button');
    btn.className = 'btn btn-lg btn-block';
    if (state.frequency === value) {
      btn.classList.add('btn-primary');
    } else {
      btn.classList.add('btn-outline');
    }
    btn.textContent = label;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('aria-pressed', String(state.frequency === value));

    btn.addEventListener('click', () => {
      state.frequency = value;

      // Set default times based on frequency
      const timesMap: Record<MedFrequency, string[]> = {
        once_daily: ['08:00'],
        twice_daily: ['08:00', '20:00'],
        three_daily: ['08:00', '14:00', '20:00'],
        four_daily: ['08:00', '12:00', '16:00', '20:00'],
        every_other_day: ['08:00'],
        weekly: ['08:00'],
        as_needed: [],
      };
      state.times = timesMap[value];
      state.step = 4;
      rerender();
    });

    grid.appendChild(btn);
  }

  wrapper.appendChild(grid);
  speak(t('setup.step3.speak', { drug: state.selectedDrug!.generic_name }));
}

function renderStep4(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const heading = document.createElement('h2');
  heading.className = 'text-center mb-md';
  heading.textContent = t('setup.step4.title');
  wrapper.appendChild(heading);

  if (state.frequency === 'as_needed') {
    const note = document.createElement('p');
    note.className = 'text-muted text-center mb-lg';
    note.textContent = t('setup.step4.asNeededNote');
    wrapper.appendChild(note);

    const nextBtn = document.createElement('button');
    nextBtn.className = 'btn btn-primary btn-lg btn-block';
    nextBtn.textContent = t('setup.step4.continue');
    nextBtn.addEventListener('click', () => {
      state.step = 5;
      rerender();
    });
    wrapper.appendChild(nextBtn);
    return;
  }

  const hint = document.createElement('p');
  hint.className = 'text-muted text-center mb-lg';
  hint.textContent = t('setup.step4.hint');
  wrapper.appendChild(hint);

  // Presets
  const presets: [string, string][] = [
    [t('setup.step4.morning'), '08:00'],
    [t('setup.step4.noon'), '12:00'],
    [t('setup.step4.afternoon'), '14:00'],
    [t('setup.step4.evening'), '18:00'],
    [t('setup.step4.bedtime'), '22:00'],
  ];

  for (let i = 0; i < state.times.length; i++) {
    const doseLabel = document.createElement('h4');
    doseLabel.className = 'mb-sm mt-md';
    doseLabel.textContent = t('setup.dose', { n: i + 1 });
    wrapper.appendChild(doseLabel);

    const presetRow = document.createElement('div');
    presetRow.className = 'flex-col gap-sm';

    for (const [presetLabel, presetValue] of presets) {
      const btn = document.createElement('button');
      btn.className = 'btn btn-block';
      if (state.times[i] === presetValue) {
        btn.classList.add('btn-primary');
      } else {
        btn.classList.add('btn-outline');
      }
      btn.textContent = presetLabel;
      btn.setAttribute('aria-pressed', String(state.times[i] === presetValue));

      const idx = i;
      btn.addEventListener('click', () => {
        state.times[idx] = presetValue;
        rerender();
      });

      presetRow.appendChild(btn);
    }

    // Custom time input
    const customRow = document.createElement('div');
    customRow.className = 'flex-row gap-md mt-sm';

    const customLabel = document.createElement('label');
    customLabel.textContent = t('setup.step4.custom');
    customLabel.className = 'text-muted';
    customLabel.style.minWidth = '100px';
    customRow.appendChild(customLabel);

    const timeInput = document.createElement('input');
    timeInput.type = 'time';
    timeInput.value = state.times[i];
    timeInput.setAttribute('aria-label', t('setup.step4.custom') + ' ' + t('setup.dose', { n: i + 1 }));
    timeInput.style.cssText = `
      flex: 1;
      min-height: 48px;
      padding: 8px 12px;
      font-size: var(--font-size-body);
      font-family: var(--font-family);
      border: 2px solid var(--color-border);
      border-radius: var(--radius-md);
      background: var(--color-bg-elevated);
      color: var(--color-text);
    `;

    const idx2 = i;
    timeInput.addEventListener('change', () => {
      state.times[idx2] = timeInput.value;
    });

    customRow.appendChild(timeInput);
    presetRow.appendChild(customRow);
    wrapper.appendChild(presetRow);
  }

  const nextBtn = document.createElement('button');
  nextBtn.className = 'btn btn-primary btn-lg btn-block mt-lg';
  nextBtn.textContent = t('setup.step4.continue');
  nextBtn.addEventListener('click', () => {
    state.step = 5;
    rerender();
  });
  wrapper.appendChild(nextBtn);

  speak(t('setup.step4.speak'));
}

function renderStep5(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const heading = document.createElement('h2');
  heading.className = 'text-center mb-md';
  heading.textContent = t('setup.step5.title');
  wrapper.appendChild(heading);

  const hint = document.createElement('p');
  hint.className = 'text-muted text-center mb-lg';
  hint.textContent = t('setup.step5.hint');
  wrapper.appendChild(hint);

  const textarea = document.createElement('textarea');
  textarea.setAttribute('aria-label', t('setup.step5.title'));
  textarea.placeholder = t('setup.step5.placeholder');
  textarea.value = state.instructions;
  textarea.rows = 3;
  textarea.style.cssText = `
    width: 100%;
    min-height: 96px;
    padding: 12px 16px;
    font-size: var(--font-size-body);
    font-family: var(--font-family);
    border: 2px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-elevated);
    color: var(--color-text);
    resize: vertical;
  `;
  textarea.addEventListener('input', () => {
    state.instructions = textarea.value;
  });
  wrapper.appendChild(textarea);

  // Voice input
  const voiceSection = document.createElement('div');
  voiceSection.className = 'text-center mt-md mb-md';

  const voiceButton = createVoiceButton(
    () => {
      voiceButton.setState('listening');
      document.dispatchEvent(new CustomEvent('aftercare:voice-start'));
    },
    () => {
      voiceButton.setState('processing');
      document.dispatchEvent(new CustomEvent('aftercare:voice-stop'));
    },
  );
  voiceSection.appendChild(voiceButton.root);
  wrapper.appendChild(voiceSection);

  const onTranscript = (e: Event): void => {
    const text = (e as CustomEvent).detail?.text ?? '';
    if (text) {
      textarea.value = text;
      state.instructions = text;
      voiceButton.setState('idle');
    }
  };
  document.addEventListener('aftercare:transcript', onTranscript);

  const btnRow = document.createElement('div');
  btnRow.className = 'flex-col gap-md mt-lg';

  const nextBtn = document.createElement('button');
  nextBtn.className = 'btn btn-primary btn-lg btn-block';
  nextBtn.textContent = t('setup.step4.continue');
  nextBtn.addEventListener('click', () => {
    state.step = 6;
    rerender();
  });
  btnRow.appendChild(nextBtn);

  const skipBtn = document.createElement('button');
  skipBtn.className = 'btn btn-outline btn-lg btn-block';
  skipBtn.textContent = t('setup.step5.skip');
  skipBtn.addEventListener('click', () => {
    state.instructions = '';
    state.step = 6;
    rerender();
  });
  btnRow.appendChild(skipBtn);

  wrapper.appendChild(btnRow);
  speak(t('setup.step5.speak'));
}

function renderStep6(wrapper: HTMLElement, state: SetupState, rerender: () => void): void {
  const heading = document.createElement('h2');
  heading.className = 'text-center mb-lg';
  heading.textContent = t('setup.step6.title');
  wrapper.appendChild(heading);

  const drug = state.selectedDrug!;
  const card = document.createElement('div');
  card.className = 'card';
  card.style.padding = 'var(--space-lg)';

  const rows: [string, string][] = [
    [t('setup.step6.medication'), drug.generic_name],
    [t('setup.step6.strength'), drug.strengths.length > 0 ? drug.strengths[0] : t('setup.step6.notSpecified')],
    [t('setup.step6.frequency'), formatFrequency(state.frequency)],
    [t('setup.step6.times'), state.times.length > 0 ? state.times.map(formatTime12h).join(', ') : t('setup.step6.asNeeded')],
  ];

  if (state.instructions) {
    rows.push([t('setup.step6.instructions'), state.instructions]);
  }

  for (const [label, value] of rows) {
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; justify-content: space-between; padding: var(--space-sm) 0; border-bottom: 1px solid var(--color-border);';

    const labelEl = document.createElement('span');
    labelEl.className = 'text-muted';
    labelEl.style.fontWeight = '600';
    labelEl.textContent = label;
    row.appendChild(labelEl);

    const valueEl = document.createElement('span');
    valueEl.style.cssText = 'text-align: right; max-width: 60%;';
    valueEl.textContent = value;
    row.appendChild(valueEl);

    card.appendChild(row);
  }

  wrapper.appendChild(card);

  const btnRow = document.createElement('div');
  btnRow.className = 'flex-col gap-md mt-lg';

  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn btn-success btn-lg btn-block';
  saveBtn.textContent = t('setup.step6.save');
  saveBtn.setAttribute('aria-label', t('setup.step6.save'));

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = t('setup.step6.saving');
    try {
      const id = crypto.randomUUID();
      addPatientMedication({
        id,
        drug_rxcui: drug.id,
        drug_name: drug.generic_name,
        strength: drug.strengths.length > 0 ? drug.strengths[0] : '',
        frequency: state.frequency,
        times: state.times,
        instructions: state.instructions,
        active: 1,
      });
      showNotification(t('setup.success', { drug: drug.generic_name }), 'success');
      speak(t('setup.successSpeak', { drug: drug.generic_name }));
      window.location.hash = '#med-list';
    } catch {
      showNotification(t('setup.error'), 'error');
      saveBtn.disabled = false;
      saveBtn.textContent = t('setup.step6.save');
    }
  });
  btnRow.appendChild(saveBtn);

  const editBtn = document.createElement('button');
  editBtn.className = 'btn btn-outline btn-lg btn-block';
  editBtn.textContent = t('setup.step6.edit');
  editBtn.addEventListener('click', () => {
    state.step = 3;
    rerender();
  });
  btnRow.appendChild(editBtn);

  wrapper.appendChild(btnRow);
  speak(t('setup.step6.speak'));
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function formatFrequency(freq: MedFrequency): string {
  const map: Record<MedFrequency, string> = {
    once_daily: t('setup.step6.freqOnce'),
    twice_daily: t('setup.step6.freqTwice'),
    three_daily: t('setup.step6.freqThrice'),
    four_daily: t('setup.step6.freqFour'),
    every_other_day: t('setup.step6.freqEveryOther'),
    weekly: t('setup.step6.freqWeekly'),
    as_needed: t('setup.step6.freqAsNeeded'),
  };
  return map[freq];
}

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return hour12 + ':' + String(m).padStart(2, '0') + ' ' + period;
}

// -----------------------------------------------------------------------
// Main render
// -----------------------------------------------------------------------

export function renderSetupScreen(container: HTMLElement): void {
  const state = defaultState();

  function render(): void {
    container.textContent = '';
    container.className = 'screen';

    const wrapper = document.createElement('div');
    wrapper.className = 'flex-col gap-md';
    wrapper.style.maxWidth = '600px';
    wrapper.style.margin = '0 auto';

    // Back / cancel button
    const topBar = document.createElement('div');
    topBar.className = 'flex-row gap-md';
    topBar.style.justifyContent = 'space-between';

    if (state.step > 1) {
      const backBtn = document.createElement('button');
      backBtn.className = 'btn btn-outline';
      backBtn.setAttribute('aria-label', t('setup.backAriaLabel'));
      backBtn.textContent = t('setup.back');
      backBtn.addEventListener('click', () => {
        state.step = Math.max(1, state.step - 1);
        render();
      });
      topBar.appendChild(backBtn);
    } else {
      topBar.appendChild(document.createElement('div')); // spacer
    }

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-muted';
    cancelBtn.textContent = t('setup.cancel');
    cancelBtn.setAttribute('aria-label', t('setup.cancelAriaLabel'));
    cancelBtn.addEventListener('click', () => {
      window.location.hash = '#home';
    });
    topBar.appendChild(cancelBtn);

    wrapper.appendChild(topBar);

    // Progress indicator
    wrapper.appendChild(renderProgress(state.step));

    // Step content
    switch (state.step) {
      case 1: renderStep1(wrapper, state, render); break;
      case 2: renderStep2(wrapper, state, render); break;
      case 3: renderStep3(wrapper, state, render); break;
      case 4: renderStep4(wrapper, state, render); break;
      case 5: renderStep5(wrapper, state, render); break;
      case 6: renderStep6(wrapper, state, render); break;
    }

    container.appendChild(wrapper);
  }

  render();
}
